import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth-store';
import { useSyncStore } from '@/offline/sync';
import { useUILayoutStore } from '@/lib/ui-layout-store';
import { useDemoMode } from '@/lib/demo-mode-store';
import { useDevicePreviewStore } from '@/lib/device-preview-store';
import { useActiveEpcrStore } from '@/lib/active-epcr-store';
import { useThemeStore } from '@/lib/theme-store';
import { cn } from '@/lib/cn';
import { Brandmark } from '@/components/Brandmark';
import { AgencyInsignia } from '@/components/AgencyInsignia';
import { DownloadCADButton } from '@/components/DownloadCADButton';
import { DownloadVitalsECGButton } from '@/components/DownloadVitalsECGButton';
import { PhoneBottomTabBar } from '@/components/tablet/PhoneBottomTabBar';
import {
  AlertTriangle,
  BookOpen,
  LayoutDashboard,
  LogOut,
  Menu,
  Network,
  Settings,
  Shield,
  Video,
  Wifi,
  Users,
  WifiOff,
  X,
  HeartHandshake,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

interface AppShellProps {
  children: ReactNode;
}

// Side-nav was previously cluttered with `New Run` and `Reports`
// items that duplicated functionality already on the home page.
// Per the simplified-flow request: Reports IS the home page (the
// list of posted + unposted incidents), and "+ Create New Incident"
// lives on the home toolbar. The remaining items are role-gated
// admin/supervisor surfaces — kept here so users with those roles
// can still reach them, but EMTs / paramedics see only Dashboard.
// `hardNav: true` flags routes that live OUTSIDE the AppShell's
// React Router tree (e.g. /preview/*, where App.tsx renders a
// different shell entirely). For those, we use a full-page
// window.location navigation so App.tsx re-evaluates its
// path-based branching (isPipelinePreview, etc). Without this,
// react-router's in-place navigate() updates the URL but App.tsx
// reads window.location.pathname only at initial mount, so the
// AppShell stays rendered and its inner Routes catch-all bounces
// the user back to /.
const navItems: Array<{
  path: string;
  icon: typeof LayoutDashboard;
  label: string;
  hardNav?: boolean;
}> = [
  // Build Logic at the top — the architecture-explainer / pitch-
  // decisive surface (NDA-gated /preview/build-logic). First slot
  // so it's the most prominent menu item; chiefs / partners / pilot
  // evaluators see it before anything else when the menu opens.
  { path: '/preview/build-logic', icon: Network, label: 'Build Logic', hardNav: true },
  { path: '/', icon: LayoutDashboard, label: 'Reports' },
  // Telehealth is the first piece of the Instar Health shared-platform
  // layer used inside EMSPCR — first EMS EHR with native video. Rooms
  // are minted by Instar's HIPAA-tier Daily.co stack via the shared
  // `telehealth-create-room` edge function.
  { path: '/telehealth', icon: Video, label: 'Telehealth' },
  { path: '/protocols', icon: BookOpen, label: 'Protocols' },
  { path: '/qa', icon: Shield, label: 'QA Review' },
  { path: '/admin/roster', icon: Users, label: 'Roster' },
  { path: '/eap', icon: HeartHandshake, label: 'EAP' },
  { path: '/admin', icon: Settings, label: 'Admin' },
];

export function AppShell({ children }: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, hasRole } = useAuthStore();
  const { online, syncing, pendingCount } = useSyncStore();
  const cockpitV2 = useThemeStore((s) => s.cockpitV2);
  const { navCollapsed, toggleNav } = useUILayoutStore();
  const demoMode = useDemoMode();
  const devicePreviewActive = useDevicePreviewStore((s) => s.preset !== 'off');
  const toggleTablet = useDevicePreviewStore((s) => s.toggleTablet);
  // Tablet Mode (device-preview frame) is a desktop-only affordance —
  // its job is to show desktop chiefs the iPad layout. On an actual
  // touch device the frame works against the user, so we hide it.
  // Detection: pointer:coarse (touch primary) + narrow viewport.
  const [isActualMobile, setIsActualMobile] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(pointer: coarse) and (max-width: 1023px)');
    const update = () => setIsActualMobile(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);
  const activeEpcr = useActiveEpcrStore((s) => s.epcr);
  const activeIncidentId = useActiveEpcrStore((s) => s.incidentId);
  const [menuOpen, setMenuOpen] = useState(false);

  // Expose the live banner height as a CSS variable so child surfaces
  // that need to fill the viewport precisely (the /protocols iframe,
  // the EPCR workflow rail, etc.) can size themselves with
  // `calc(100dvh - var(--shell-banner-h))` and stay perfectly
  // contained when the two-row banner reflows on mobile. Using
  // ResizeObserver keeps it correct on orientation change and when
  // the active-EPCR import buttons appear / disappear.
  const headerRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof window === 'undefined') return;
    const apply = (h: number) => {
      document.documentElement.style.setProperty('--shell-banner-h', `${Math.round(h)}px`);
    };
    apply(el.offsetHeight);
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) apply(entry.contentRect.height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The drawer is the only nav surface — keep it closed across route
  // transitions so the next screen doesn't open under a stale overlay.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Nav gating: only the admin console requires an admin role.
  // Every other surface (Build Logic, Reports, Telehealth, Protocols,
  // QA Review, Roster, EAP) is visible to anyone who has signed in
  // for the pilot — chiefs, town administrators, supervisors,
  // providers. Pilot evaluators expect a feature-complete tour.
  const filteredNav = navItems.filter((item) => {
    if (item.path === '/admin') return hasRole('admin');
    return true;
  });

  return (
    <div className="flex h-[100dvh] flex-col safe-area-inset">
      {/* Top Bar — heavy-metal: diamond-plate steel surface + a thin
          red accent stripe along the bottom edge for FIRE-side
          identity. Two intentional rows so the layout is identical
          on phone, tablet, and desktop:
            ROW 1: menu | square dept logo | EMSPCR wordmark | (user info on lg) | logout
            ROW 2: demo mode | CAD | Vitals/ECG | (Tablet Mode on desktop) | online LED
       */}
      <header ref={headerRef} className="relative diamond-plate flex flex-col gap-1.5 px-3 sm:px-4 py-2 shadow-md min-w-0">
        {/* ROW 1 — identity */}
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {/* Single slide-out toggle — works on every breakpoint. The
              rail is now a drawer (overlay + backdrop) on phone, tablet,
              and desktop alike. */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="min-h-touch min-w-touch flex items-center justify-center hover:bg-black/20 text-slate-100 rounded shrink-0"
            aria-label={menuOpen ? 'Close navigation' : 'Open navigation'}
            aria-expanded={menuOpen}
            aria-controls="primary-nav"
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
          <AgencyInsignia />
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <Brandmark size={32} />
            <div className="flex flex-col leading-none min-w-0">
              <span
                className={cn(
                  "text-base tracking-tight text-slate-100 truncate",
                  cockpitV2 && "[font-family:var(--font-display)]",
                )}
              >
                <span className="font-black">EMS</span><span className="font-normal">PCR</span><span className="wordmark-pulse text-brand-400 font-bold">.</span>
              </span>
              <span className="text-[9px] font-mono uppercase tracking-widest text-slate-400 truncate">
                Records Mgmt System
              </span>
            </div>
          </div>

          {/* User info — agency badge, desktop only */}
          <div className="hidden lg:flex items-center gap-2 shrink-0">
            <div className="text-right">
              <div className="text-xs font-semibold text-slate-200 leading-tight">{user?.full_name}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500 leading-tight">
                {user?.cert_level} · {user?.badge_number}
              </div>
            </div>
          </div>

          <button
            onClick={() => { void signOut(); }}
            className="min-h-touch min-w-touch flex items-center justify-center rounded-sm hover:bg-slate-800 shrink-0"
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut size={18} className="text-slate-400" />
          </button>
        </div>

        {/* ROW 2 — actions */}
        <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
          {/* DEMO MODE toggle — amber when active. */}
          <button
            onClick={demoMode.toggle}
            className={cn(
              'flex items-center justify-center px-3 py-1.5 text-[11px] font-black uppercase tracking-widest transition-colors shrink-0 rounded-sm',
              demoMode.enabled
                ? 'bg-amber-500 text-black hover:bg-amber-400'
                : 'border border-slate-700 text-slate-400 hover:bg-slate-800 hover:text-slate-200',
            )}
            aria-label={demoMode.enabled ? 'Exit demo mode' : 'Enter demo mode'}
            title={demoMode.enabled ? 'Demo mode ON — click to exit' : 'Enter demo / training mode'}
          >
            Demo
          </button>

          {/* Per-EPCR import buttons — visible on every breakpoint
              once an EPCR is open. The component-level styles handle
              touch sizing. */}
          {activeEpcr && activeIncidentId && (
            <>
              <DownloadCADButton incidentId={activeIncidentId} />
              <DownloadVitalsECGButton incidentId={activeIncidentId} epcrId={activeEpcr.id} />
            </>
          )}

          {/* TABLET MODE toggle — desktop-only. Pointer-coarse +
              narrow viewport hides this on actual mobile/tablet. */}
          {!isActualMobile && (
            <button
              onClick={toggleTablet}
              className={cn(
                'sparkle-border flex items-center justify-center px-3 py-1.5 text-[11px] font-black uppercase tracking-widest transition-colors shrink-0 rounded-sm',
                devicePreviewActive
                  ? 'bg-sky-500 text-black hover:bg-sky-400'
                  : 'text-slate-300 hover:bg-slate-800 hover:text-slate-100',
              )}
              aria-label={devicePreviewActive ? 'Exit tablet preview' : 'Enter tablet preview'}
              title={devicePreviewActive ? 'Tablet mode ON — click to exit' : 'Preview the tablet (iPad) layout from desktop'}
            >
              Tablet Mode
            </button>
          )}

          {/* Spacer pushes the LED to the right. */}
          <span className="flex-1" aria-hidden />

          {/* Online/offline indicator. With cockpit-v2 on, this becomes
              a true bezel-light pip — a small lit indicator with a
              colored glow, like the lit hardware lamp on an apparatus
              dashboard. With v2 off, falls back to the existing wifi
              icon. The queued-count chip is intentionally gone in
              both modes; that information lives on the EPCR
              transmission pill where it's actionable. */}
          <div
            className="flex items-center gap-1 shrink-0"
            title={[
              online ? 'Online' : 'Offline (changes queue locally)',
              pendingCount > 0
                ? syncing ? 'Sync in progress' : `${pendingCount} queued`
                : null,
            ].filter(Boolean).join(' · ')}
            aria-label={online ? 'Online' : 'Offline'}
          >
            {cockpitV2 ? (
              <span
                aria-hidden
                className={cn(
                  'inline-block w-2 h-2 rounded-full border',
                  'border-[var(--cockpit-edge-deep)]',
                )}
                style={{
                  backgroundColor: online
                    ? 'var(--bezel-light-on)'
                    : 'var(--console-red)',
                  boxShadow: online
                    ? '0 0 6px var(--bezel-light-glow), 0 1px 1px rgba(0,0,0,0.5) inset'
                    : '0 0 4px rgba(185,78,78,0.55), 0 1px 1px rgba(0,0,0,0.5) inset',
                }}
              />
            ) : online ? (
              <Wifi size={16} className="text-green-400" />
            ) : (
              <WifiOff size={16} className="text-amber-400" />
            )}
          </div>
        </div>

        {/* Heavy-metal accent stripe — thin painted band along the
            bottom edge of the top bar. */}
        <span aria-hidden className="absolute inset-x-0 bottom-0 h-1 accent-stripe-fire" />
      </header>

      <div className="flex flex-1 overflow-hidden relative">
        {/* Side Navigation — slide-out drawer on every breakpoint.
            Default closed; the dashboard / detail surfaces own the full
            viewport width. One toggle button (top-left) opens & closes
            on phone, tablet, and desktop the same way. */}
        <nav
          id="primary-nav"
          className={cn(
            // Sits BELOW the top bar (top-14 ≈ 56px header height),
            // capped at viewport-height-minus-header. Width has TWO
            // modes:
            //   expanded:  w-44 (176px) — full label + icon
            //   collapsed: w-14 (56px)  — icons only, label hidden
            // Both modes still slide in/out via the menu button.
            'rail-cockpit fixed left-0 z-40 overflow-y-auto transition-all duration-200 ease-out',
            'max-w-[78vw]',
            navCollapsed ? 'w-14' : 'w-44',
            menuOpen ? 'translate-x-0 shadow-2xl' : '-translate-x-full',
          )}
          aria-hidden={menuOpen ? undefined : 'true'}
          style={{
            top: 'var(--shell-banner-h, 56px)',
            maxHeight: 'calc(100dvh - var(--shell-banner-h, 56px))',
          }}
        >
          <span aria-hidden className="absolute inset-y-0 right-0 w-1 accent-stripe-fire" />

          {!navCollapsed && (
            <div className="rail-section-label px-3 pt-3 pb-2">
              Navigation
            </div>
          )}

          <div className={cn(
            'flex flex-col gap-1.5 pb-3',
            navCollapsed ? 'px-1.5 pt-3' : 'px-2',
          )}>
            {filteredNav.map((item) => {
              const active = location.pathname === item.path;
              const className = cn(
                'rail-button flex w-full items-center text-left font-semibold min-h-touch no-underline',
                active && 'rail-button-active',
                navCollapsed
                  ? 'justify-center px-2 py-2.5'
                  : 'gap-3 px-3 py-2.5 text-[15px]',
              );
              const inner = (
                <>
                  <item.icon size={20} className="shrink-0" />
                  {!navCollapsed && <span className="truncate">{item.label}</span>}
                </>
              );

              // hardNav routes (e.g. /preview/*) live OUTSIDE
              // AppShell's React Router tree. Render as a native
              // <a href> so the browser does a full page navigation:
              //   - works even if JS errors / service worker stalls
              //   - supports middle-click / cmd-click to open in
              //     a new tab (impossible with <button>)
              //   - App.tsx re-mounts and re-evaluates isPipelinePreview
              //     instead of being stuck on the previous branch
              if (item.hardNav) {
                return (
                  <a
                    key={item.path}
                    href={item.path}
                    title={navCollapsed ? item.label : undefined}
                    aria-label={item.label}
                    className={className}
                    onClick={() => setMenuOpen(false)}
                  >
                    {inner}
                  </a>
                );
              }

              // SPA-internal route — react-router navigate, no full
              // page reload.
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setMenuOpen(false);
                  }}
                  title={navCollapsed ? item.label : undefined}
                  aria-label={item.label}
                  className={className}
                >
                  {inner}
                </button>
              );
            })}
          </div>

          {!online && (
            <div className={cn(
              'rounded border border-amber-700 bg-amber-950/80 mb-2',
              navCollapsed ? 'mx-1.5 px-1.5 py-1.5' : 'mx-2 mt-2 mb-3 px-2 py-1.5',
            )}>
              <div className={cn(
                'flex items-center text-amber-300 text-xs font-semibold',
                navCollapsed ? 'justify-center' : 'gap-1.5',
              )}>
                <AlertTriangle size={13} className="shrink-0" />
                {!navCollapsed && <span>Offline — will sync</span>}
              </div>
            </div>
          )}

          {/* Collapse / expand toggle — pinned at the bottom. Tap to
              flip between icon-only and full-label modes. State is
              persisted in the layout store. */}
          <button
            type="button"
            onClick={toggleNav}
            title={navCollapsed ? 'Expand rail' : 'Collapse rail to icons'}
            aria-label={navCollapsed ? 'Expand rail' : 'Collapse rail to icons'}
            className={cn(
              'rail-button mt-auto mx-2 mb-3 flex items-center min-h-touch',
              navCollapsed
                ? 'justify-center px-2 py-2'
                : 'gap-2 px-3 py-2 text-xs uppercase tracking-widest',
            )}
          >
            {navCollapsed ? <ChevronsRight size={18} /> : <ChevronsLeft size={16} />}
            {!navCollapsed && <span>Collapse</span>}
          </button>
        </nav>

        {/* Overlay backdrop — every breakpoint when drawer is open. */}
        {menuOpen && (
          <div
            className="fixed inset-0 z-30 bg-black/50"
            onClick={() => setMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        {/* Main Content owns the full canvas. The drawer overlays;
            content does not shift, so input fields & dashboards always
            render at full width. Bottom padding on phones reserves
            space for the PhoneBottomTabBar (56px nav + iPhone home-
            indicator safe-area). The bar itself is ipad:hidden so
            tablets / desktops get the standard padding. */}
        <main
          // Phone reserves bottom space for the tab bar; iPad+ uses
          // the standard padding (the `ipad:` variant resets to a
          // normal value, since the bar is hidden there).
          className={cn(
            'flex-1 overflow-y-auto p-4 md:p-5 lg:p-6 xl:p-8',
            'pb-[calc(56px+env(safe-area-inset-bottom)+1rem)]',
            'ipad:pb-5 lg:pb-6 xl:pb-8',
          )}
        >
          {children}
        </main>
      </div>

      {/* Phone-only bottom tab bar (Finding 25). Hidden on iPad+. */}
      <PhoneBottomTabBar onOpenMenu={() => setMenuOpen(true)} />
    </div>
  );
}
